import { useState, useEffect } from 'react';
import axios from 'axios';
import config from '../config';

// 서버가 답을 못 한 경우(망 오류·5xx)는 '권한 없음'이 아니다 — 실제 판정은 로그인·서버 쪽에서 한다.
// WHY: 권한 없음으로 처리하면 유저 API 재시작 몇 초 사이나 망 오류 때 들어온 사람까지 빈 화면을 봤다.
const isServerUnavailable = (error) => !error.response || error.response.status >= 500;

/**
 * 도메인 접근 권한 체크 훅
 */
const useDomainPermission = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [permissionLoading, setPermissionLoading] = useState(true);
  const [permissionError, setPermissionError] = useState(null);
  const [permissionDetails, setPermissionDetails] = useState(null);

  useEffect(() => {
    checkDomainPermission();
  }, []);

  const checkDomainPermission = async () => {
    try {
      setPermissionLoading(true);
      
      // 현재 도메인 URL
      const currentDomain = window.location.origin;
      const domainName = window.location.hostname; // 도메인 이름만 추출
      console.log('[DomainPermission] Checking domain:', currentDomain, 'hostname:', domainName);
      
      // 로컬 개발 환경 체크 (localhost는 권한 체크 스킵)
      if (currentDomain.includes('localhost') || currentDomain.includes('127.0.0.1')) {
        console.log('개발 환경: 도메인 권한 체크 스킵');
        setHasPermission(true);
        setPermissionDetails({ 
          accessType: 'development',
          message: '개발 환경에서는 권한 체크를 스킵합니다.'
        });
        setPermissionLoading(false);
        return;
      }

      // API URL 설정 - config에서 가져오기
      const API_BASE_URL = config.API_URL;
      
      // 토큰 확인
      const token = localStorage.getItem('token');
      
      // JWT 토큰 만료 체크
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          const expiry = payload.exp * 1000; // exp는 초 단위
          if (Date.now() > expiry) {
            console.log('토큰 만료됨 - 삭제');
            localStorage.removeItem('token');
            localStorage.removeItem('userId');
            // 토큰 만료되었으므로 도메인 존재 여부만 체크
          }
        } catch (e) {
          console.error('토큰 파싱 실패:', e);
          localStorage.removeItem('token');
        }
      }
      
      // 유효한 토큰이 없는 경우 - 도메인 존재 여부만 체크
      const validToken = localStorage.getItem('token'); // 위에서 삭제되었을 수 있음
      if (!validToken) {
        console.log('비로그인 상태: 도메인 존재 여부 체크');
        
        try {
          // 도메인이 존재하고 할당되어 있는지 확인
          const response = await axios.get(
            `${API_BASE_URL}/domain-access/check-domain-exists/${encodeURIComponent(domainName)}`
          );
          
          if (response.data.success && response.data.domainExists && response.data.hasAssignments) {
            console.log('도메인이 존재하고 할당됨 - 접근 허용');
            setHasPermission(true);
            setPermissionDetails({ 
              accessType: 'guest',
              message: '로그인이 필요합니다.'
            });
          } else if (response.data.success && response.data.domainExists && !response.data.hasAssignments) {
            console.log('도메인은 존재하지만 할당 없음 - 접근 차단');
            setHasPermission(false);
            setPermissionError('도메인에 할당된 사용자가 없습니다.');
          } else {
            console.log('도메인이 존재하지 않음 - 접근 차단');
            setHasPermission(false);
            setPermissionError('등록되지 않은 도메인입니다.');
          }
        } catch (error) {
          console.error('도메인 존재 확인 실패:', error);
          if (isServerUnavailable(error)) {
            setHasPermission(true);
            setPermissionLoading(false);
            return;
          }
          if (error.response?.status === 404) {
            setPermissionError('등록되지 않은 도메인입니다.');
          } else {
            setPermissionError('도메인 확인 중 오류가 발생했습니다.');
          }
          setHasPermission(false);
        }
        
        setPermissionLoading(false);
        return;
      }

      // 로그인한 사용자 - 도메인 권한 확인
      console.log('로그인 상태: 도메인 권한 체크', {
        currentDomain,
        API_BASE_URL,
        token: 'exists'
      });
      
      const response = await axios.get(
        `${API_BASE_URL}/domain-access/check/${encodeURIComponent(domainName)}`,
        {
          headers: {
            'Authorization': `Bearer ${validToken}`
          }
        }
      );

      if (response.data.success && response.data.hasAccess) {
        console.log('도메인 접근 권한 확인:', response.data);
        setHasPermission(true);
        setPermissionDetails({
          accessType: response.data.accessType,
          inheritedFrom: response.data.inheritedFrom,
          message: response.data.accessType === 'direct' 
            ? '직접 할당된 도메인입니다.' 
            : `${response.data.inheritedFrom}님으로부터 상속된 권한입니다.`
        });
      } else {
        console.log('도메인 접근 권한 없음:', response.data);
        // 도메인 권한이 없으면 권한 없음 상태로 설정
        setHasPermission(false);
        setPermissionError('이 도메인에 대한 접근 권한이 없습니다.');
        
        // 토큰 삭제 (리다이렉트는 App.js에서 처리)
        localStorage.clear();
        sessionStorage.clear();
      }
    } catch (error) {
      console.error('도메인 권한 확인 실패:', error);
      if (isServerUnavailable(error)) {
        setHasPermission(true);
        return;
      }
      
      // 404 오류: 도메인이 등록되지 않음
      if (error.response?.status === 404) {
        setPermissionError('등록되지 않은 도메인입니다.');
      } 
      // 401 오류: 인증 실패
      else if (error.response?.status === 401) {
        setPermissionError('인증이 만료되었습니다. 다시 로그인해주세요.');
        localStorage.removeItem('token');
      }
      // 기타 오류
      else {
        setPermissionError('도메인 권한 확인 중 오류가 발생했습니다.');
      }
      
      setHasPermission(false);
    } finally {
      setPermissionLoading(false);
    }
  };

  return {
    hasPermission,
    permissionLoading,
    permissionError,
    permissionDetails,
    checkDomainPermission
  };
};

export default useDomainPermission;